import { useEffect, useRef, useState } from 'react'
import { fetchDirectionList } from '@api/directions'
import type { DirectionListItem } from '@api/directions'
import { useLang } from '@shared/i18n/useLang'
import { graphqlLanguageCode } from '@shared/i18n/languages'
import { ssrCache } from '@shared/lib/ssrCache'

export function useDirectionList() {
  const lang = useLang()
  const code = graphqlLanguageCode(lang)
  const cacheKey = `directions:${code}`
  const seeded = ssrCache.has(cacheKey)

  const [directions, setDirections] = useState<DirectionListItem[]>(
    () => ssrCache.get<DirectionListItem[]>(cacheKey) ?? [],
  )
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>(seeded ? 'ok' : 'loading')
  // Skips the refetch right after prerendered/hydrated markup already has
  // this language's data, so hydration doesn't flash back to a loading state.
  const skipNextFetch = useRef(seeded)

  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return }
    setStatus('loading')
    fetchDirectionList(code)
      .then(data => { setDirections(data); setStatus('ok') })
      .catch(() => setStatus('error'))
  }, [lang, code])

  return { directions, status }
}
